import {
    deleteAssessmentTemplatesId,
    getAssessmentTemplates,
    getAssessmentTemplatesId,
    postAssessmentTemplates,
    putAssessmentTemplatesId,
    type AssessmentTemplateSchema,
    type AssessmentTemplatesListResponseSchema,
    type CreateAssessmentTemplateBodySchema,
    type UpdateAssessmentTemplateBodySchema,
} from '@/generated/api-client'
import { configureOpenApiClient } from '@/lib/api/client/configure-openapi-client'
import { parseAxiosError } from '@/utils/parse-axios-error.util'

configureOpenApiClient()

export type AssessmentTemplatesListQueryParams = {
    companySlug: string
    page?: number | string
    limit?: number | string
    search?: string
    skipPagination?: boolean | string
}

export type AssessmentTemplatesListResult = {
    rows: AssessmentTemplateSchema[]
    total: number
    page: number
    limit: number
}

function toQueryString(value: number | string | boolean | undefined): string | undefined {
    if (value === undefined || value === null || value === '') return undefined
    return String(value)
}

export async function fetchAssessmentTemplatesList(query: AssessmentTemplatesListQueryParams): Promise<AssessmentTemplatesListResult> {
    try {
        const { data } = await getAssessmentTemplates({
            query: {
                companySlug: query.companySlug,
                page: toQueryString(query.page),
                limit: toQueryString(query.limit),
                search: query.search || undefined,
                skipPagination:
                    query.skipPagination === true || query.skipPagination === 'true' ? 'true' : query.skipPagination === 'false' ? 'false' : undefined,
            },
            throwOnError: true,
        })

        const envelope = data as AssessmentTemplatesListResponseSchema
        const payload = envelope.data
        return {
            rows: payload?.AssessmentTemplates ?? [],
            total: payload?.total ?? 0,
            page: payload?.page ?? 1,
            limit: payload?.limit ?? Number(query.limit ?? 10),
        }
    } catch (error) {
        throw new Error(parseAxiosError(error, 'Failed to fetch assessment templates'))
    }
}

export async function fetchAssessmentTemplateById(id: number | string, companySlug: string): Promise<AssessmentTemplateSchema> {
    try {
        const { data } = await getAssessmentTemplatesId({
            path: { id: String(id) },
            query: { companySlug },
            throwOnError: true,
        })
        const envelope = data as { data?: AssessmentTemplateSchema }
        if (!envelope?.data) {
            throw new Error('Assessment template not found')
        }
        return envelope.data
    } catch (error) {
        throw new Error(parseAxiosError(error, 'Failed to fetch assessment template'))
    }
}

export async function createAssessmentTemplate(body: CreateAssessmentTemplateBodySchema): Promise<{ template: AssessmentTemplateSchema; message?: string }> {
    try {
        const { data } = await postAssessmentTemplates({
            body,
            throwOnError: true,
        })
        const envelope = data as { data?: AssessmentTemplateSchema; message?: string }
        if (!envelope?.data) {
            throw new Error(envelope?.message || 'Failed to create assessment template')
        }
        return { template: envelope.data, message: envelope.message }
    } catch (error) {
        throw new Error(parseAxiosError(error, 'Failed to create assessment template'))
    }
}

export async function deleteAssessmentTemplate(
    id: number | string,
    params: { companySlug: string }
): Promise<{ template: AssessmentTemplateSchema; message?: string }> {
    try {
        const { data } = await deleteAssessmentTemplatesId({
            path: { id: String(id) },
            query: { companySlug: params.companySlug },
            throwOnError: true,
        })
        const envelope = data as { data?: AssessmentTemplateSchema; message?: string }
        return {
            template: envelope?.data as AssessmentTemplateSchema,
            message: envelope?.message,
        }
    } catch (error) {
        throw new Error(parseAxiosError(error, 'Failed to delete assessment template'))
    }
}
